import { type LucideIcon } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';

import { useCurrentUser } from '@/hooks/useCurrentUser';

import { canAccessComponent, hasAnyPermission } from '@/lib/permissions';

type NavGroup = {
  title?: string;
  requiredPermissions?: string[];
  items: {
    title: string;
    url: string;
    icon?: LucideIcon;
    requiredPermissions?: string[];
  }[];
};

export function NavMain({ groups }: { groups: NavGroup[] }) {
  const location = useLocation();

  const { userData } = useCurrentUser();

  return (
    <div className="flex flex-col gap-4 pb-3 pt-2 px-0 w-full">
      {groups.map(
        group =>
          hasAnyPermission(userData, group.requiredPermissions) && (
            <div key={`${group.title}`} className="flex flex-col gap-0.5 items-start w-full">
              {group.title && (
                <div className="flex items-center h-7 overflow-hidden px-2 py-0 rounded-lg w-full">
                  <div className="flex items-center grow min-h-0 min-w-0 px-2.5 py-1.5 rounded-lg">
                    <p className="text-xs font-bold text-foreground grow min-h-0 min-w-0">
                      {group.title}
                    </p>
                  </div>
                </div>
              )}
              {group.items.map(item => {
                const active =
                  item.url === '/'
                    ? location.pathname === '/'
                    : location.pathname.startsWith(item.url);
                const hasPermission = canAccessComponent(userData, {
                  requiredPermissions: item.requiredPermissions,
                });

                if (!hasPermission) {
                  return null;
                }

                return (
                  <div key={item.title} className="flex items-center px-2 py-0 w-full">
                    <Link
                      to={item.url}
                      className={`flex gap-2 grow h-9 items-center min-h-0 min-w-0 overflow-hidden px-2.5 py-1.5 rounded-lg transition-colors ${
                        active
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'hover:bg-accent/50 text-foreground hover:text-accent-foreground'
                      }`}
                    >
                      <div className="overflow-hidden shrink-0 size-[18px]">
                        {item.icon && (
                          <item.icon className="size-[18px] transition-colors" strokeWidth={1.25} />
                        )}
                      </div>
                      <p className="text-sm font-normal leading-5 grow min-h-0 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap transition-colors">
                        {item.title}
                      </p>
                    </Link>
                  </div>
                );
              })}
            </div>
          )
      )}
    </div>
  );
}
